import { storeToRefs } from "pinia"

import type { Step } from "@/config/steps"
import { sleep } from "@/lib/utils"
import { useI18nStore } from "@/stores/i18n"
import { waitForAssessmentLoadingDebugRelease } from "@/features/assessment-loading/assessmentLoadingDebug"

type AssessmentLoadingStep =
  | Extract<Step, "SWIPE" | "DRIVE" | "BRAIN" | "VOICE" | "MOVE" | "MOVEBIS">
  | "SCAN"

export const assessmentLoadingMinDurationMs: Record<
  AssessmentLoadingStep,
  number
> = {
  SWIPE: 5000,
  DRIVE: 5000,
  BRAIN: 5000,
  VOICE: 5000,
  MOVE: 5000,
  MOVEBIS: 5000,
  SCAN: 5500,
}

/**
 * Tient l'écran de chargement à l'écran pendant toute son animation, et garde
 * le sélecteur de langue fermé aussi longtemps.
 *
 * Les chargeurs décident de l'ouverture du sélecteur au moment où l'API leur
 * répond — « le test n'a pas encore commencé, la langue est encore
 * changeable » — c'est-à-dire souvent plusieurs secondes avant la fin de
 * l'animation. Le verrou couvre cet écart sans toucher à `showLang` : chaque
 * décision d'écran reste intacte et reprend la main une fois l'animation
 * finie, y compris pour les assessments dont le chargeur n'en prend aucune.
 */
export async function withAssessmentLoadingMinDuration<T>(
  assessment: AssessmentLoadingStep,
  loadAssessmentData: () => Promise<T>
) {
  const { isLangLocked } = storeToRefs(useI18nStore())
  isLangLocked.value = true

  try {
    const [result] = await Promise.all([
      loadAssessmentData(),
      sleep(assessmentLoadingMinDurationMs[assessment]),
    ])

    await waitForAssessmentLoadingDebugRelease(assessment)

    return result
  } finally {
    // `finally` et pas seulement le chemin nominal : un chargeur qui échoue
    // laisserait sinon le sélecteur verrouillé pour le reste de la session.
    isLangLocked.value = false
  }
}
